import styled, { css } from 'styled-components';
import rem from 'polished/lib/helpers/rem';
import { TabList, TabPanel, Tab } from 'react-tabs';
// template components
import Col from '@nmx/template/dist/componentLibrary/foundations/Grid/Col';
import Row from '@nmx/template/dist/componentLibrary/foundations/Grid/Row';
import SectionContainer from '@nmx/template/dist/componentLibrary/components/SectionContainer';
// nm2024 themed components
import { BulletedListNm2024 } from '../../../../nm2024Theme/components/bulletedList/styles';
import { LinkNm2024 } from '../../../../nm2024Theme/components/link/styles';
// NM 2024 theme
import Nm2024Theme from '../../../../nm2024Theme/nm2024Theme/colors';

export const AnchorTabButtonStyled = styled.a`
  ${({ theme }) => {
    const xlargeUp = theme.xlargeUp();
    const mediumUp = theme.mediumUp();
    const smallUp = theme.smallUp();
    let fontSize = rem(16);
    let lineHeight = rem(20);

    if(xlargeUp){
      fontSize = rem(24);
      lineHeight = rem(24);
    }else if(mediumUp){
      fontSize = rem(22);
      lineHeight = rem(22);
    }

    return css`
      position: relative;
      display: ${smallUp ? 'inline-flex' : 'flex'};
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: ${Nm2024Theme.neutral.grayLight10}; /** text color when unselected  */
      font-family: 'Source Sans', 'Helvetica Neue', Helvetica, sans-serif;
      font-size: ${fontSize};
      font-weight: 400;
      line-height: ${lineHeight};
      text-decoration: none;
      text-align: center;
      border: none;
      white-space: nowrap;

      &.nmx-tab-button__financial-planning {
        margin: 0 auto;
      }

      &:hover,
      &:focus {
        && {
          font-weight: 500;
          text-decoration: none;
          color: ${theme.colors.neutral.white}; /** text color when hovered  */
          cursor: pointer;
        }
      }
    `;
  }
}`;

export const BulletedListStyled = styled(BulletedListNm2024)`
  ${({ theme }) => {
    const mediumUp = theme.mediumUp();
    const smallUp = theme.smallUp();
    let thisMargin = 0;
    const horizontalPadding = rem(6);

    if(mediumUp){
      thisMargin = 'auto';
    }else if(smallUp){
      thisMargin = '0 5%';
    }

    return css`
      color: ${theme.colors.neutral.grayDark30};
      margin: ${thisMargin};
      padding-bottom: ${rem(28)};
      ${mediumUp && 'width: fit-content;'}
      .nmx-icon {
        height: ${rem(25)};
        width: ${rem(25)};
        background-color: ${Nm2024Theme.navy.navy};
        border-radius: 50%;
        padding-left: ${horizontalPadding};
        padding-right: ${horizontalPadding};
        top: ${rem(1)};
        .icon-fill {
          fill: ${theme.colors.neutral.white};
        }
      }
    `;
  }
}`;

export const ColContentContainerStyled = styled(Col)`
  z-index: 1;
`;

export const ColNestedStyled = styled(Col)`
  padding: 0 ${rem(16)};
  text-align: left;
`;

export const ColTabsContainerStyled = styled(Col)`
  ${({ theme }) => {
    const smallDown = theme.smallDown();
    return css`
      display: flex;
      ${smallDown && `height: ${rem(65)};`}
      padding: 0;
      margin: 0;
      background-color: ${Nm2024Theme.navy.navy};
      z-index: 1;
    `;
  }
}`;

export const DivTabContentContainerStyled = styled.div`
  margin-top: ${rem(32)};
`;

export const LinkStyledButton = styled(LinkNm2024)`
  ${({ theme }) => {
    const smallDown = theme.smallDown();
    return css`
      display: flex;
      margin: 0 auto ${rem(24)};
      ${smallDown && `
        width: 100%;
        max-width: none;
      `}
    `;
  }
}`;

export const LinkStyledCta = styled(LinkNm2024)`
  width: max-content;
  margin: 0 auto;
  text-align: center;
  display: block;
  ::after {
    margin-top: 0;
  }
`;

export const RowNestedStyled = styled(Row)`
  width: 100%;
  max-width: ${rem(1200)};
  margin: 0 auto;
`;

export const SectionContainerStyled = styled(SectionContainer)`
 ${({ theme }) => {
    const mediumUp = theme.mediumUp();
    return css`
      position: relative;
      && {
        padding-top: 0;
      }
      padding-bottom: ${mediumUp ? rem(52) : rem(48)};
      .nmx-container {
        max-width: none;
        padding: 0;
        @media all and (-ms-high-contrast: none) {
          height: 100%;
        }
      }
    `;
  }
}`;

export const TabListStyled = styled(TabList)`
  ${({ theme }) => {
    const mediumUp = theme.mediumUp();
    const smallUp = theme.smallUp();

    return css`
      max-width: ${mediumUp ? rem(800) : rem(500)};
      margin: 0 auto;
      padding: 0;
      list-style: none;
      display: flex;
      justify-content: space-between;
      width: 100%;
      padding: 0 ${smallUp ? rem(30) : rem(15)};
    `;
  }
}`;

export const TabPanelStyled = styled(TabPanel)`
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  &.react-tabs__tab-panel--selected {
    animation-name: fadeIn;
    animation-duration: 300ms;
    animation-timing-function: linear;
  }
`;

export const TabStyled = styled(Tab)`
  ${({ theme }) => {
    const xlargeUp = theme.xlargeUp();
    const mediumUp = theme.mediumUp();
    const smallUp = theme.smallUp();
    let thisHeight = rem(65);
    let horizontalPadding = rem(5);
    let borderBottomPos = rem(20);

    if(xlargeUp){
      thisHeight = rem(75);
    } else if(smallUp){
      thisHeight = rem(72);
    }

    if (mediumUp){
      horizontalPadding = rem(60);
    }

    if(mediumUp){
      borderBottomPos = rem(20);
    } else if (smallUp){
      borderBottomPos = rem(24);
    }

    return css`
      display: inline-flex;
      width: ${mediumUp ? '33%' : 'auto'}; /** // was wrapping at 33.3% // could probably try the calc(100/3) trick, but we can revisit. */
      height: ${thisHeight};
      max-height: 100%;
      padding: 0 ${horizontalPadding};
      vertical-align: top;
      align-self: center;
      justify-content: ${mediumUp ? 'center' : 'space-evenly'};
      letter-spacing: ${mediumUp ? rem(1) : 0};
      &.react-tabs__tab--selected {
        background-color: ${Nm2024Theme.navy.navy};
        text-decoration: none;
        .nmx-tab-button {
          font-weight: 500;
          background-color: ${Nm2024Theme.navy.navy};
          color: ${theme.colors.neutral.white}; /** text color when selected  */
        }
        a::after {
          content: '';
          width: 100%;
          position: absolute;
          bottom: ${borderBottomPos};
          border-bottom: ${rem(1)} solid ${theme.colors.neutral.white};
        }
      }
    `;
  }
}`;
